import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Scale, Lock, Mail, Eye, EyeOff, AlertCircle, CheckCircle, Building2, User, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AttorneySignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ firm_name: "", contact_name: "", email: "", password: "", contact_phone: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/attorney/register`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-7 h-7 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Registration received</h1>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            Thanks for registering <strong>{form.firm_name}</strong>. Your account is <strong>pending approval</strong> by our
            team. Once approved, you'll be able to log in and start receiving matched leads.
          </p>
          <button onClick={() => navigate("/attorney/login")}
            className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition">
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/25 flex items-center justify-center mb-4">
            <Scale className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Create a Firm Account</h1>
          <p className="text-sm text-muted-foreground mt-1">ICantFindALawyer.com — Partner Access</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-8 shadow-lg">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm mb-5">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input icon={Building2} label="Firm Name" value={form.firm_name} onChange={set("firm_name")} placeholder="Smith & Associates" required />
            <Input icon={User} label="Contact Name" value={form.contact_name} onChange={set("contact_name")} placeholder="Jane Smith" />
            <Input icon={Mail} type="email" label="Firm Email" value={form.email} onChange={set("email")} placeholder="firm@lawoffice.com" required autoComplete="email" />
            <Input icon={Phone} label="Contact Phone" value={form.contact_phone} onChange={set("contact_phone")} placeholder="(555) 123-4567" />
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed mt-2">
              {loading ? "Creating account..." : "Create Account"}
            </button>
          </form>
          <p className="text-[11px] text-muted-foreground/70 mt-4 text-center">
            New accounts are reviewed and approved before they can receive leads.
          </p>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Already have an account?{" "}
          <button onClick={() => navigate("/attorney/login")} className="text-primary hover:underline">Sign in</button>
        </p>
      </div>
    </div>
  );
}

function Input({ icon: Icon, label, ...props }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input {...props}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition" />
      </div>
    </div>
  );
}
